import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Pet } from '../types/pet';
import {
  createPet as createPetApi,
  CreatePetRequest,
  getPets as getPetsApi,
  updatePetDid as updatePetDidApi,
  addFamilyMember as addFamilyMemberApi,
  removeFamilyMember as removeFamilyMemberApi,
  searchPets as searchPetsApi,
  AddFamilyMemberRequest,
  RemoveFamilyMemberRequest,
  SearchPetsRequest,
  SearchPetsResponse,
  getPetDraft as getPetDraftApi,
  deletePetDraft as deletePetDraftApi,
  PetDraft,
} from '../../../api/pets';
import { useAuth } from '../../../context/AuthContext';

interface CreatePetResult {
  petId: string;
  reward?: {
    amount: number;
    message: string;
  };
}

interface PetsContextType {
  pets: Pet[];
  isLoading: boolean;
  error: string | null;
  selectedPetId: string | null;
  selectedPet: Pet | null;
  setSelectedPetId: (petId: string) => void;
  addPet: (pet: Pet) => void;
  addPetToApi: (petData: CreatePetRequest) => Promise<CreatePetResult>;
  updatePetDid: (petId: string, did: string) => Promise<void>;
  addFamilyMember: (
    petId: string,
    data: AddFamilyMemberRequest
  ) => Promise<void>;
  removeFamilyMember: (
    petId: string,
    data: RemoveFamilyMemberRequest
  ) => Promise<void>;
  searchPets: (data: SearchPetsRequest) => Promise<SearchPetsResponse>;
  updatePet: (petId: string, updates: Partial<Pet>) => void;
  getPetById: (petId: string) => Pet | undefined;
  getAllPets: () => Pet[];
  refreshPets: () => Promise<void>;
  // Draft related
  petDraft: PetDraft | null;
  hasDraft: boolean;
  refreshDraft: () => Promise<void>;
  deleteDraft: () => Promise<void>;
}

const PetsContext = createContext<PetsContextType | undefined>(undefined);

export function PetsProvider({ children }: { children: ReactNode }) {
  const [pets, setPets] = useState<Pet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPetId, setSelectedPetIdRaw] = useState<string | null>(() => {
    try { return localStorage.getItem('petso_selected_pet_id') || null; } catch { return null; }
  });
  const setSelectedPetId = (id: string) => {
    setSelectedPetIdRaw(id);
    try { localStorage.setItem('petso_selected_pet_id', id); } catch {}
  };
  const [petDraft, setPetDraft] = useState<PetDraft | null>(null);
  const { user } = useAuth();

  // Computed: get the selected pet object
  const selectedPet = selectedPetId
    ? pets.find((pet) => pet.id === selectedPetId) || null
    : pets[0] || null;

  // Auto-select first pet once when pets first load. Defer setState to avoid update during commit (max update depth in commitDeletionEffects).
  const autoSelectDoneRef = useRef(false);
  const firstPetId = pets.length > 0 ? pets[0].id : null;
  useEffect(() => {
    if (autoSelectDoneRef.current || selectedPetId || !firstPetId) return;
    autoSelectDoneRef.current = true;
    const id = firstPetId;
    setTimeout(() => setSelectedPetId(id), 0);
  }, [firstPetId, selectedPetId]);

  // Fetch pets from API (with timeout to prevent indefinite loading)
  const PETS_FETCH_TIMEOUT_MS = 15000;
  const refreshPets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('Pets fetch timeout')),
          PETS_FETCH_TIMEOUT_MS
        )
      );
      const response = await Promise.race([getPetsApi(), timeoutPromise]);
      // Transform API response to match Pet interface
      const apiPets: Pet[] = response.pets.map((pet) => ({
        id: pet.id,
        did: pet.did || '',
        didStatus: pet.didStatus,
        didRevokedAt: pet.didRevokedAt,
        name: pet.name,
        species: pet.species as 'dog' | 'cat',
        breed: pet.breed,
        dateOfBirth: pet.dateOfBirth || '',
        gender: pet.gender,
        color: pet.color || '',
        microchipId: pet.microchipId || '',
        owner: {
          name: user?.name || '',
          email: user?.email || '',
        },
        parents: pet.parents,
        maternalGrandmother: pet.maternalGrandmother,
        maternalGrandfather: pet.maternalGrandfather,
        paternalGrandmother: pet.paternalGrandmother,
        paternalGrandfather: pet.paternalGrandfather,
        siblings: pet.siblings,
        country: pet.country,
        city: pet.city,
        imageUrl: pet.imageUrl,
        photoKey: pet.photoKey,
        aiEnabled: pet.aiEnabled,
        aiSettings: pet.aiSettings
          ? {
              allowProfessionalView: pet.aiSettings.allowProfessionalView,
              activatedDate: '',
            }
          : undefined,
        // DID Permissions
        didPermissionsViewData: pet.didPermissionsViewData,
        didPermissionsAmendFields: pet.didPermissionsAmendFields,
        didPermissionsIssueCredentials: pet.didPermissionsIssueCredentials,
        didPermissionsTimeBound: pet.didPermissionsTimeBound,
        didPermissionsExpiryDate: pet.didPermissionsExpiryDate,
        // Birth / Litter
        ownershipStatus: pet.ownershipStatus,
        isDeceased: pet.isDeceased,
        // Support both camelCase (lostStatus) and snake_case (lost_status) from API
        lostStatus:
          pet.lostStatus ??
          (pet as { lost_status?: string }).lost_status ??
          undefined,
        isCaretaker: pet.isCaretaker,
      }));

      // Use only API data - no mock data merging
      setPets(apiPets);
    } catch (err) {
      console.error('Failed to fetch pets:', err);
      setError('Failed to load pets from server');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch draft from API
  const refreshDraft = useCallback(async () => {
    try {
      const response = await getPetDraftApi();
      setPetDraft(response.draft);
    } catch (err) {
      console.error('Failed to fetch pet draft:', err);
      // Non-critical, don't set error state
    }
  }, []);

  // Delete draft
  const deleteDraft = useCallback(async () => {
    try {
      await deletePetDraftApi();
      setPetDraft(null);
    } catch (err) {
      console.error('Failed to delete pet draft:', err);
    }
  }, []);

  // Load pets and draft once on mount. Defer to avoid any setState (e.g. setIsLoading) during effect run → prevents max update depth in commit phase.
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    setTimeout(() => {
      refreshPets();
      refreshDraft();
    }, 0);
  }, [refreshPets, refreshDraft]);

  // Add pet locally (for backwards compatibility)
  const addPet = (pet: Pet) => {
    setPets((prevPets) => [...prevPets, pet]);
    console.log('Pet added to context:', pet);
  };

  // Add pet via API
  const addPetToApi = async (
    petData: CreatePetRequest
  ): Promise<CreatePetResult> => {
    setError(null);
    try {
      const response = await createPetApi(petData);
      console.log('Pet created via API:', response);

      if (!response?.pet?.id) {
        const msg =
          typeof response?.message === 'string'
            ? response.message
            : 'Invalid response from server';
        throw new Error(msg);
      }

      return {
        petId: response.pet.id,
        reward: response.reward,
      };
    } catch (err) {
      console.error('Failed to create pet via API:', err);
      setError('Failed to create pet');
      throw err;
    }
  };

  // Update pet DID via API
  const updatePetDid = async (petId: string, did: string): Promise<void> => {
    setError(null);
    try {
      await updatePetDidApi(petId, did);
      // Update local state
      setPets((prevPets) =>
        prevPets.map((pet) => (pet.id === petId ? { ...pet, did } : pet))
      );
      console.log('Pet DID updated:', petId, did);
    } catch (err) {
      console.error('Failed to update pet DID:', err);
      setError('Failed to update pet DID');
      throw err;
    }
  };

  const addFamilyMember = async (
    petId: string,
    data: AddFamilyMemberRequest
  ): Promise<void> => {
    setError(null);
    try {
      await addFamilyMemberApi(petId, data);
      await refreshPets();
      console.log('Family member added and pets refreshed');
    } catch (err) {
      console.error('Failed to add family member:', err);
      setError('Failed to add family member');
      throw err;
    }
  };

  const removeFamilyMember = async (
    petId: string,
    data: RemoveFamilyMemberRequest
  ): Promise<void> => {
    setError(null);
    try {
      await removeFamilyMemberApi(petId, data);
      await refreshPets();
      console.log('Family member removed and pets refreshed');
    } catch (err) {
      console.error('Failed to remove family member:', err);
      setError('Failed to remove family member');
      throw err;
    }
  };

  const searchPets = async (
    data: SearchPetsRequest
  ): Promise<SearchPetsResponse> => {
    try {
      return await searchPetsApi(data);
    } catch (err) {
      console.error('Failed to search pets:', err);
      throw err;
    }
  };

  const updatePet = useCallback((petId: string, updates: Partial<Pet>) => {
    setPets((prevPets) =>
      prevPets.map((pet) => (pet.id === petId ? { ...pet, ...updates } : pet))
    );
  }, []);

  const getPetById = useCallback(
    (petId: string) => pets.find((pet) => pet.id === petId),
    [pets]
  );

  const getAllPets = useCallback(() => pets, [pets]);

  return (
    <PetsContext.Provider
      value={{
        pets,
        isLoading,
        error,
        selectedPetId,
        selectedPet,
        setSelectedPetId,
        addPet,
        addPetToApi,
        updatePetDid,
        addFamilyMember,
        removeFamilyMember,
        searchPets,
        updatePet,
        getPetById,
        getAllPets,
        refreshPets,
        // Draft related
        petDraft,
        hasDraft: petDraft !== null && !!petDraft.petName,
        refreshDraft,
        deleteDraft,
      }}
    >
      {children}
    </PetsContext.Provider>
  );
}

export function usePets() {
  const context = useContext(PetsContext);
  if (context === undefined) {
    throw new Error('usePets must be used within a PetsProvider');
  }
  return context;
}
